import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Chip,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import axios from 'axios';
import MuiAlert from '@mui/material/Alert';

const Alert = MuiAlert;

const AgentInformationModal = ({ open, onClose, agentId, onAgentUpdated }) => {
  const navigate = useNavigate();
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  // Form state (migrated from AgentInfo.js)
  const [form, setForm] = useState({
    id: 0,
    agentName: '',
    description: '',
    agentDomain: '',
    active: true,
  });
  const [knowledgeBasesOptions, setKnowledgeBasesOptions] = useState([]);
  const [selectedKbs, setSelectedKbs] = useState([]); // Always KB objects
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [nameError, setNameError] = useState('');

  // 1. Fetch KBs and Forms when modal opens
  useEffect(() => {
    if (open) {
      setSnackbar({ open: false, message: '', severity: 'success' });
      const fetchKbs = async () => {
        try {
          setLoading(true);
          const response = await axios.post('/kbs/getAllKbs');
          setKnowledgeBasesOptions(response.data || []);
        } catch (error) {
          setError('Failed to load knowledge bases');
        } finally {
          setLoading(false);
        }
      };
      const fetchForms = async () => {
        try {
          setLoading(true);
          const response = await axios.post('/form/getAll');
          setForms(response.data || []);
        } catch (err) {
          setError('Failed to load forms. Please try again later.');
          setForms([]);
        } finally {
          setLoading(false);
        }
      };
      fetchKbs();
      fetchForms();
      if (!agentId) {
        setForm({
          id: 0,
          agentName: '',
          description: '',
          agentDomain: '',
          active: true
        });
        setSelectedKbs([]);
        setSelectedFormId(null);
      }
    }
  }, [open, agentId]);

  // 2. Fetch agent details only after KBs are loaded
  useEffect(() => {
    if (open && agentId && agentId && knowledgeBasesOptions.length > 0) {
      const fetchAgentDetail = async () => {
        try {
          setLoading(true);
          const response = await axios.post('/agents/getAgentDetail', null, { params: { agentId: agentId } });
          setForm(response.data);
          if (response.data.kbs && response.data.kbs.length > 0) {
            // Map selected KBs to KB objects from knowledgeBasesOptions
            setSelectedKbs(
              knowledgeBasesOptions.filter(kb =>
                response.data.kbs.some(sel => (sel.id ? sel.id === kb.id : sel === kb.id))
              )
            );
          }
          if (response.data.formId) {
            setSelectedFormId(response.data.formId);
          }
        } catch (error) {
          setError('Error fetching agent details');
        } finally {
          setLoading(false);
        }
      };
      fetchAgentDetail();
    }
  }, [open, agentId, knowledgeBasesOptions.length]);

  // Form field change handler
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  // Removed: handleKnowledgeBasesChange. Always store KB objects in selectedKbs.

  // Validation for required fields in create mode
  const validate = () => {
    if (!form.agentName) return 'Agent name is required.';
    if (!form.description) return 'Description is required.';
    if (!selectedKbs || selectedKbs.length === 0) return 'Knowledge Bases are required.';
    return null;
  };

  // Check if all required fields are filled (for button enable)
  const isCreateEnabled = form.agentName && form.description && selectedKbs && selectedKbs.length > 0;

  const checkAgentExists = async (name) => {
    if (!name.trim()) {
      setNameError('');
      return;
    }

    try {
      const response = await axios.post('/agents/agentExists', {
        agentName: name,
        id: agentId || null
      });

      if (response.data.exists) {
        setNameError('Agent name already exists');
      } else {
        setNameError('');
      }
    } catch (error) {
      console.error('Error checking agent name:', error);
    }
  };

  // Save/update handler
  const handleUpdate = async () => {
    if (nameError) {
      setSnackbar({ open: true, message: 'Please fix the agent name error', severity: 'error' });
      return;
    }

    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      const { connectionId: _omitConnectionId, ...formWithoutToolConnections } = form;
      const updatedFormData = {
        ...formWithoutToolConnections,
        kbs: selectedKbs.map(kb => ({ id: kb.id })), // correct for backend
        formId: selectedFormId
      };
      const res = await axios.post('/agents/saveAgent', updatedFormData);
      setSnackbar({ open: true, message: 'Agent updated successfully!', severity: 'success' });
      onAgentUpdated && onAgentUpdated(res.data);
      setError(null);

      if (agentId) {
        // Delay closing the modal until after Snackbar is shown
        setTimeout(() => {
          onClose && onClose();
        }, 2000); // Match Snackbar autoHideDuration
      } else {
        navigate('/Agent/' + res.data.agentId);
      }
    } catch (err) {
      const d = err?.response?.data;
      const apiMsg = typeof d === 'string' ? d : d?.message;
      const fallback = 'Failed to update agent.';
      setSnackbar({ open: true, message: apiMsg || fallback, severity: 'error' });
      setError(apiMsg || fallback);
    } finally {
      setLoading(false);
    }
  };

  // Cancel/close logic for newAgent mode
  const handleCancel = () => {
    if (!agentId) {
      setShowUnsavedDialog(true);
    } else {
      onClose && onClose();
    }
  };

  const handleCloseIcon = () => {
    handleCancel();
  };

  const handleUnsavedDialogClose = () => {
    setShowUnsavedDialog(false);
  };

  const handleUnsavedDialogContinue = () => {
    setShowUnsavedDialog(false);
    onClose && onClose();
    navigate('/Agents');
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="xl"
        fullWidth
        sx={{ zIndex: 99999 }}
        PaperProps={{ style: { minWidth: 1200, minHeight: 800, zIndex: 99999 } }}
      >
        <DialogTitle sx={{ position: 'relative', pr: 6 }}>
          {!agentId ? 'Add Agent Information' : 'Edit Agent Information'}
          <IconButton
            onClick={handleCloseIcon}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#888', background: '#f5f5f5', zIndex: 21000 }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Multilingual &amp; Voice Support:</strong> AI agents can understand and respond in dozens of languages, enabling natural conversations with users around the world. Agents can communicate through both text and voice, helping you create localized experiences, support global audiences, and engage customers in their preferred language—all from a single AI assistant.
          </Alert>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Agent Name"
                name="agentName"
                value={form.agentName}
                onChange={(e) => {
                  handleChange(e);
                  setNameError('');
                }}
                onBlur={(e) => checkAgentExists(e.target.value)}
                required
                fullWidth
                error={!!nameError}
                helperText={nameError}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.active}
                    onChange={handleChange}
                    name="active"
                    color="primary"
                  />
                }
                label={form.active ? 'Active' : 'Inactive'}
              />
              <TextField
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                multiline
                required
                rows={3}
                fullWidth
              />
              {/* // Agent Domain field removed */}
              {/* Knowledge Bases Autocomplete (mirrored from Agents.js) */}
              <Autocomplete
                multiple
                id="knowledge-bases-selector"
                options={knowledgeBasesOptions}
                getOptionLabel={(option) => option.kbName || option.name || ''}
                value={selectedKbs}
                onChange={(event, newValue) => setSelectedKbs(newValue)} // Always KB objects
                disablePortal
                PopperProps={{ sx: { zIndex: 100000 } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Knowledge Bases"
                    placeholder="Select knowledge bases"
                    required
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option.kbName || option.name} {...getTagProps({ index })} key={option.id} />
                  ))
                }
              />
              <Autocomplete
                id="forms-selector"
                options={forms}
                getOptionLabel={(option) => option.name || ''}
                value={forms.find((option) => option.id === selectedFormId) || null}
                onChange={(event, newValue) => { setSelectedFormId(newValue && newValue.id) }}
                disablePortal
                PopperProps={{ sx: { zIndex: 100000 } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Form"
                    placeholder="Select form"
                  />
                )}
              />
              {error && (
                <Typography color="error" variant="body2">{error}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, py: 2, px: 3 }}>
          {!agentId ? (
            <Button onClick={handleUpdate} variant="contained" color="primary" disabled={!isCreateEnabled || loading} sx={{ minWidth: 220, py: 1.2 }}>
              CREATE AND CUSTOMIZE AGENT
            </Button>
          ) : (
            <Button onClick={handleUpdate} variant="contained" color="primary" disabled={loading} sx={{ minWidth: 180, py: 1.2 }}>
              UPDATE &amp; Close
            </Button>
          )}
          {/* Cancel button removed as requested */}
        </DialogActions>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Dialog>
      {/* Unsaved changes warning dialog for newAgent mode */}
      <Dialog open={showUnsavedDialog}
        sx={{ zIndex: 99999 }}
        onClose={handleUnsavedDialogClose}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            It looks like your Agent hasn't been saved, if you continue you will lose your progress.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnsavedDialogClose} color="primary" variant="outlined">Stay</Button>
          <Button onClick={handleUnsavedDialogContinue} color="error" variant="contained">Lose Changes</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgentInformationModal;
